import { LocalDate, ZonedDateTime } from "@js-joda/core";
import "@js-joda/timezone";
import { PrimaryButton } from "components/designV2/buttons/primaryButton";
import { SecondaryButton } from "components/designV2/buttons/secondaryButton";
import { DatePicker } from "components/designV2/datePicker/datePicker";
import { InputField } from "components/designV2/inputfield/inputfield";
import { TimePicker } from "components/designV2/timePicker/timePicker";
import { ColorV2 } from "config/colors";
import { CompanyGigShift } from "model/Gig";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useCompanyStore } from "web-apps/company/stores/companyStore/companyStore";
import type { Worker } from "../../../organizationalStructure/chooseWorkersModal.types";
import type { FormData } from "../modal/gigFormModal.types";
import {
  BreakFieldsRow,
  ShiftActionButtonsRow,
  ShiftActionRow,
  ShiftDateWrap,
  ShiftField,
  ShiftFieldsRow,
  ToggleTimeIntervalText,
} from "./shiftForm.styled";
import {
  buildShiftsFromDates,
  calculateBreakMinutesForDate,
  type CompanySetting,
  computeEarliestSelectableDate,
  getNonOverlappingShifts,
  getCompanySettingNumber,
  getCompanySettingString,
  isBreakWithinShiftValid,
  parseZoneId,
  safeParseInt,
} from "./shiftForm.logic";

export type ShiftsFormData = {
  startTime: string;
  endTime: string;
  breakTimeMin: number;
  breakStartTime: string;
  breakEndTime: string;
  deductionAmount: number;
};

type Props = {
  setFormData: React.Dispatch<React.SetStateAction<FormData>>;
  selectedWorkers: Worker[];
  showCancelButton?: boolean;
  onCancelAdd?: () => void;
  onShiftCreated?: () => void;
};

/** Shift form used to create one or multiple gig shifts. */
export const ShiftForm: React.FC<Props> = (props) => {
  const { t } = useTranslation();
  const [companyState] = useCompanyStore();

  const companySettings = useMemo<CompanySetting[]>(() => {
    return (
      companyState.companySettings ??
      companyState.company?.companySettings ??
      []
    );
  }, [companyState.companySettings, companyState.company?.companySettings]);

  const companyTimeZone = useMemo(
    () =>
      getCompanySettingString(
        companySettings,
        "CompanyTimeZone",
        "Europe/Stockholm",
      ),
    [companySettings],
  );

  const companyDeductionAmount = useMemo(
    () => getCompanySettingNumber(companySettings, "GigPayDeduction", 0),
    [companySettings],
  );

  const companyBreakTime = useMemo(
    () => getCompanySettingNumber(companySettings, "GigBreakTime", 0),
    [companySettings],
  );

  const companyGigShiftStartTime = useMemo(
    () =>
      getCompanySettingString(
        companySettings,
        "CompanyGigShiftStartTime",
        "08:00",
      ),
    [companySettings],
  );

  const companyGigShiftEndTime = useMemo(
    () =>
      getCompanySettingString(
        companySettings,
        "CompanyGigShiftEndTime",
        "17:00",
      ),
    [companySettings],
  );

  const zoneId = useMemo(() => parseZoneId(companyTimeZone), [companyTimeZone]);
  const nowTime = useMemo(() => ZonedDateTime.now(zoneId), [zoneId]);
  const [showTimeIntervall, setShowTimeIntervall] = useState(false);
  const [shiftDates, setShiftDates] = useState<LocalDate[]>([]);
  const [datePickerResetKey, setDatePickerResetKey] = useState(0);

  const initialStartISO = useMemo(
    () => companyGigShiftStartTime.slice(0, 5),
    [companyGigShiftStartTime],
  );

  const initialEndISO = useMemo(
    () => companyGigShiftEndTime.slice(0, 5),
    [companyGigShiftEndTime],
  );

  const [formData, setFormData] = useState<ShiftsFormData>({
    startTime: initialStartISO,
    endTime: initialEndISO,
    breakTimeMin: companyBreakTime,
    breakStartTime: nowTime.plusHours(1).toLocalTime().toString().slice(0, 5),
    breakEndTime: nowTime.plusHours(1).toLocalTime().toString().slice(0, 5),
    deductionAmount: companyDeductionAmount,
  });

  const defaultsRef = useRef<{
    startTime: string;
    endTime: string;
    breakTimeMin: number;
    deductionAmount: number;
  }>({
    startTime: initialStartISO,
    endTime: initialEndISO,
    breakTimeMin: companyBreakTime,
    deductionAmount: companyDeductionAmount,
  });

  useEffect(() => {
    const previousDefaults = defaultsRef.current;
    setFormData((previousFormData) => ({
      ...previousFormData,
      startTime:
        previousFormData.startTime === previousDefaults.startTime
          ? initialStartISO
          : previousFormData.startTime,
      endTime:
        previousFormData.endTime === previousDefaults.endTime
          ? initialEndISO
          : previousFormData.endTime,
      breakTimeMin:
        previousFormData.breakTimeMin === previousDefaults.breakTimeMin
          ? companyBreakTime
          : previousFormData.breakTimeMin,
      deductionAmount:
        previousFormData.deductionAmount === previousDefaults.deductionAmount
          ? companyDeductionAmount
          : previousFormData.deductionAmount,
    }));

    defaultsRef.current = {
      startTime: initialStartISO,
      endTime: initialEndISO,
      breakTimeMin: companyBreakTime,
      deductionAmount: companyDeductionAmount,
    };
  }, [
    initialStartISO,
    initialEndISO,
    companyBreakTime,
    companyDeductionAmount,
  ]);

  /** Calculates break duration in minutes for a date and optional interval. */
  const calculateBreakMinutes = useCallback(
    (
      date: LocalDate,
      breakStartTime?: string,
      breakEndTime?: string,
    ): number => {
      return calculateBreakMinutesForDate({
        date,
        breakStartTime,
        breakEndTime,
        zoneId,
      });
    },
    [zoneId],
  );

  useEffect(() => {
    if (!showTimeIntervall) return;
    const { breakStartTime, breakEndTime } = formData;
    if (!breakStartTime || !breakEndTime) return;

    const baseDate = shiftDates[0] ?? nowTime.toLocalDate();
    const newValue =
      breakStartTime === breakEndTime
        ? 0
        : calculateBreakMinutes(baseDate, breakStartTime, breakEndTime);

    setFormData((prev) =>
      prev.breakTimeMin !== newValue
        ? { ...prev, breakTimeMin: newValue }
        : prev,
    );
  }, [
    showTimeIntervall,
    formData.breakStartTime,
    formData.breakEndTime,
    shiftDates,
    nowTime,
    calculateBreakMinutes,
  ]);

  /** Creates shifts from form values and appends non-overlapping shifts. */
  const handleCreateShifts = useCallback(() => {
    const newShifts: CompanyGigShift[] = buildShiftsFromDates({
      dates: shiftDates,
      startTime: formData.startTime,
      endTime: formData.endTime,
      breakStartTime: formData.breakStartTime,
      breakEndTime: formData.breakEndTime,
      breakTimeMin: formData.breakTimeMin,
      deductionAmount: formData.deductionAmount,
      zoneId,
    });

    let addedAny = false;
    props.setFormData((prev) => {
      const filteredNewShifts = getNonOverlappingShifts({
        existingShifts: prev.shifts,
        incomingShifts: newShifts,
      });

      if (filteredNewShifts.length === 0) {
        return prev;
      }
      addedAny = true;

      return {
        ...prev,
        shifts: [...prev.shifts, ...filteredNewShifts],
      };
    });

    setShiftDates([]);
    setDatePickerResetKey((k) => k + 1);
    if (addedAny) {
      props.onShiftCreated?.();
    }
  }, [shiftDates, formData, zoneId, props.setFormData, props.onShiftCreated]);

  /** Validates that break interval is non-negative and within shift boundaries. */
  const validateBreakWithinShift = useCallback((): boolean => {
    const baseDate = shiftDates[0] ?? nowTime.toLocalDate();
    return isBreakWithinShiftValid({
      startTime: formData.startTime,
      endTime: formData.endTime,
      breakStartTime: formData.breakStartTime,
      breakEndTime: formData.breakEndTime,
      breakTimeMin: formData.breakTimeMin,
      showTimeInterval: showTimeIntervall,
      baseDate,
      zoneId,
    });
  }, [formData, showTimeIntervall, shiftDates, nowTime, zoneId]);

  const earliestSelectableDate = useMemo(
    () => computeEarliestSelectableDate(zoneId, props.selectedWorkers ?? []),
    [zoneId, props.selectedWorkers],
  );

  /** Handles date range changes from the date picker. */
  const handleRangeChange = useCallback((dates: LocalDate[]): void => {
    setShiftDates(dates);
  }, []);

  return (
    <>
      <ShiftDateWrap>
        <DatePicker
          key={datePickerResetKey}
          label={t("CreateShift.Date")}
          value={shiftDates[0] ?? null}
          onRangeChange={handleRangeChange}
          allowRangePicker
          allowMultiSelectInSingleMode
          earliestSelectableDate={earliestSelectableDate}
          showExcludeToggles
        />
      </ShiftDateWrap>

      <ShiftFieldsRow>
        <ShiftField>
          <TimePicker
            label={`${t("StampClock.StartTime")}*`}
            value={formData.startTime}
            onChange={(value) =>
              setFormData((p) => ({ ...p, startTime: value }))
            }
            fullWidth
          />
        </ShiftField>
        <ShiftField>
          <TimePicker
            label={`${t("StampClock.EndTime")}*`}
            value={formData.endTime}
            onChange={(value) => setFormData((p) => ({ ...p, endTime: value }))}
            fullWidth
          />
        </ShiftField>
        <ShiftField>
          <InputField
            label={t("CreateShift.DeductionAmount")}
            value={String(formData.deductionAmount)}
            onChange={(value) => {
              const parsed = safeParseInt(value, 0);
              setFormData((p) => ({
                ...p,
                deductionAmount: Math.max(0, parsed),
              }));
            }}
            type="number"
            fullWidth
          />
        </ShiftField>

        {!showTimeIntervall && (
          <ShiftField>
            <InputField
              label={t("CreateShift.BreakMin")}
              value={
                formData.breakTimeMin != null
                  ? String(formData.breakTimeMin)
                  : ""
              }
              onChange={(value) => {
                const parsed = safeParseInt(value, 0);
                setFormData((p) => ({
                  ...p,
                  breakTimeMin: Math.max(0, parsed),
                }));
              }}
              type="number"
              fullWidth
            />
          </ShiftField>
        )}
      </ShiftFieldsRow>

      <ToggleTimeIntervalText
        typography="s1"
        color={ColorV2.Blue2}
        onClick={() => setShowTimeIntervall((s) => !s)}
      >
        {!showTimeIntervall
          ? t("CreateShift.SetTimeIntervall")
          : t("CreateShift.ClickToEnterMinutesBreak")}
      </ToggleTimeIntervalText>

      {showTimeIntervall && (
        <BreakFieldsRow>
          <ShiftField>
            <TimePicker
              label={t("StampClock.StartTime")}
              value={formData.breakStartTime}
              onChange={(value) =>
                setFormData((p) => ({ ...p, breakStartTime: value }))
              }
              fullWidth
            />
          </ShiftField>
          <ShiftField>
            <TimePicker
              label={t("StampClock.EndTime")}
              value={formData.breakEndTime}
              onChange={(value) =>
                setFormData((p) => ({ ...p, breakEndTime: value }))
              }
              fullWidth
            />
          </ShiftField>
        </BreakFieldsRow>
      )}

      <ShiftActionRow>
        <ShiftActionButtonsRow>
          {props.showCancelButton && (
            <SecondaryButton
              label={t("General.Cancel")}
              size="small"
              onClick={props.onCancelAdd}
            />
          )}
          <PrimaryButton
            label={t("CreateShift.AddShift")}
            onClick={handleCreateShifts}
            disabled={shiftDates.length === 0 || !validateBreakWithinShift()}
            size="small"
          />
        </ShiftActionButtonsRow>
      </ShiftActionRow>
    </>
  );
};
